import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import Lottie from 'lottie-react';
import testmo from '../testmoni.json';
import '../csscomponent/Testimonial.css';
import { getTestimonials } from '../lib/sqlAuth';
import { subscribeCmsUpdates } from '../lib/cmsSync';
import { publicAsset } from '../lib/publicPath';
import fallbackTestimonials from '../data/Testimonial.json';

const FALLBACK_ITEMS = Array.isArray(fallbackTestimonials)
  ? fallbackTestimonials.map((item, index) => ({
      id: `fallback-${index}`,
      name: item.name,
      role: item.role,
      quote: item.quote,
      image: item.image,
    }))
  : [];

function Testimonial() {
  const [items, setItems] = useState([]);
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      try {
        const res = await getTestimonials();
        if (!mounted) return;
        const next = Array.isArray(res?.items) && res.items.length > 0 ? res.items : FALLBACK_ITEMS;
        setItems(next);
        setCurrent((prev) => (next.length ? Math.min(prev, next.length - 1) : 0));
      } catch (_) {
        if (mounted) setItems(FALLBACK_ITEMS);
      }
    };

    load();
    const unsubscribe = subscribeCmsUpdates((payload) => {
      if (!payload?.scope || ['site', 'testimonials'].includes(payload.scope)) {
        load();
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const total = items.length;

  useEffect(() => {
    if (total <= 1) return undefined;
    const timer = setInterval(() => {
      setDirection(1);
      setCurrent((prev) => (prev + 1) % total);
    }, 4500);
    return () => clearInterval(timer);
  }, [total]);

  if (!total) return null;

  const nextSlide = () => {
    setDirection(1);
    setCurrent((prev) => (prev + 1) % total);
  };

  const prevSlide = () => {
    setDirection(-1);
    setCurrent((prev) => (prev - 1 + total) % total);
  };

  const active = items[current];
  const imageSrc = active?.image
    ? (active.image.startsWith('http') || active.image.startsWith('/uploads/')
      ? active.image
      : publicAsset(active.image))
    : publicAsset('images/image.png');

  const variants = {
    enter: (dir) => ({
      x: dir > 0 ? 70 : -70,
      opacity: 0,
      scale: 0.96,
    }),
    center: {
      x: 0,
      opacity: 1,
      scale: 1,
      transition: { duration: 0.45, ease: 'easeOut' },
    },
    exit: (dir) => ({
      x: dir > 0 ? -70 : 70,
      opacity: 0,
      scale: 0.96,
      transition: { duration: 0.35, ease: 'easeIn' },
    }),
  };

  return (
    <section className="testimonial-section" id="testimonials">
      <div className="testimonial-header">
        <div className="testimonial-copy">
          <p>Testimonials</p>
          <h2>What clients say about working with us</h2>
        </div>
        <div className="testimonial-badge-card">
          <div className="testimonial-badge-icon" aria-hidden="true">
            ✓
          </div>
          <div>
            <span className="testimonial-badge-label">Trusted Reviews</span>
            <strong>Verified Client Feedback</strong>
          </div>
        </div>
      </div>

      <div className="testimonial-stage">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.article
            key={`${active.name}-${current}`}
            className="testimonial-card"
            custom={direction}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
          >
            <div className="testimonial-meta">
              <div className="testimonial-person">
                <img src={imageSrc} alt={active.name} className="avatar" />
                <div>
                  <h3>{active.name}</h3>
                  <span>{active.role}</span>
                </div>
              </div>
              <div className="testimonial-inline-animation">
                <Lottie animationData={testmo} loop className="testimonial-lottie" />
              </div>
            </div>
            <blockquote>{active.quote}</blockquote>
          </motion.article>
        </AnimatePresence>
      </div>

      <div className="testimonial-controls">
        <button type="button" className="testimonial-arrow" onClick={prevSlide} aria-label="Previous testimonial">
          &#8249;
        </button>
        <div className="dots">
          {items.map((item, index) => (
            <button
              key={`${item.id}-${index}`}
              type="button"
              className={`dot ${index === current ? 'active' : ''}`}
              onClick={() => {
                setDirection(index > current ? 1 : -1);
                setCurrent(index);
              }}
              aria-label={`Show testimonial ${index + 1}`}
            />
          ))}
        </div>
        <button type="button" className="testimonial-arrow" onClick={nextSlide} aria-label="Next testimonial">
          &#8250;
        </button>
      </div>
    </section>
  );
}

export default Testimonial;
